import { Form, IconButton } from "@/components/form"
import { faMinusCircle, faPlusCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { ref } from "vue"
import { defineComponent, inferProps, useCustomValidation, type Component, type ReactiveComponent } from "vue-utils"
import { type GenericDisplayProps } from "../displays"
import type { GenericInputProps } from "./InputComponent"

type Props<T> = GenericInputProps<(T | null)[]>

const gridStyles = css`
	display: grid;
	grid-template-columns: 1fr 4rem 4rem;
	gap: 0.5rem;
`

export function createFilterArrayInput<T>(
	InputComponent: Component<GenericInputProps<T>>,
	DisplayComponent: Component<GenericDisplayProps<T>>
) {
	const FilterArrayInput: ReactiveComponent<Props<T>> = (props) => {
		useCustomValidation(() => {
			if (addingValue.value) {
				return "Please confirm the new value."
			}
			if (props.value === null || props.value.length === 0) {
				return "Please enter at least 1 value."
			}
			return true
		})
		const addingValue = ref<boolean>(props.value === null)
		const value = ref<T>()

		function pushValue(value: T) {
			const newSet = new Set([...(props.value ?? [])])
			newSet.add(value)
			props.setValue(Array.from(newSet))
		}

		function removeValue(index: number) {
			props.setValue(props.value?.filter((_, i) => i !== index) ?? [])
		}

		function resetValue() {
			addingValue.value = false
			value.value = undefined
		}

		return () => (
			<div>
				{props.value !== null && (
					<table
						class={`table small table-striped table-hover grid-table`}
						style={{
							gridTemplateColumns: `1fr max-content`,
						}}
					>
						<tbody>
							{props.value.map((value, index) => (
								<tr key={index}>
									<td class="d-flex align-items-center">
										<DisplayComponent value={value} />
									</td>
									<td class="d-flex align-items-center justify-content-center">
										<IconButton
											style="width: 4rem;"
											icon={faMinusCircle}
											color="danger"
											onClick={() => removeValue(index)}
											disabled={props.value!.length === 1}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}

				{addingValue.value ? (
					<Form class={`${gridStyles} me-2`}>
						<InputComponent value={value.value ?? null} setValue={(newValue) => (value.value = newValue)} />
						<IconButton
							icon={faPlusCircle}
							type="button"
							color="success"
							onClick={() => {
								pushValue(value.value!)
								resetValue()
							}}
							disabled={value.value === undefined || value.value === null}
						/>
						<IconButton
							icon={faXmarkCircle}
							type="button"
							color="danger"
							onClick={resetValue}
							disabled={props.value === null || props.value?.length === 0}
						/>
					</Form>
				) : (
					<IconButton
						style="width: 100%"
						icon={faPlusCircle}
						color="success"
						onClick={() => (addingValue.value = true)}
						type="button"
					>
						Add Value
					</IconButton>
				)}
			</div>
		)
	}

	return defineComponent(FilterArrayInput, inferProps<Props<T>>())
}
